import React, { useEffect, useMemo, useState, type JSX } from "react";
import type { AdminRecognitionRawAccessGrant, AdminRecognitionRawAccessRequest } from "../../contracts/admin-read-models";
import { pickAdminDashboardLocaleText } from "../../shared/locale/admin-dashboard-copy";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import { diffJson } from "./recognition-json-diff";
import type { RecognitionAuditRawActionViewModel } from "./recognition-audit-view-model";
import { validateRecognitionRawAccessReason } from "./recognition-audit-raw-access-dialog";

export type RecognitionRawArtifact = {
  action: RecognitionAuditRawActionViewModel;
  id: string;
  kind: "json" | "source" | "text";
  label: string;
  mimeType?: string;
};

export type RecognitionRawArtifactGrantTarget = Pick<
  AdminRecognitionRawAccessRequest,
  "recognitionRunId" | "targetKind" | "targetId"
>;

export function isMatchingRawArtifactGrant(
  expected: RecognitionRawArtifactGrantTarget,
  grant: AdminRecognitionRawAccessGrant,
): boolean {
  return grant.recognitionRunId === expected.recognitionRunId
    && grant.targetKind === expected.targetKind
    && grant.targetId === expected.targetId;
}

type LoadedArtifact = {
  artifact: RecognitionRawArtifact;
  grant: AdminRecognitionRawAccessGrant;
  json?: unknown;
  status: "loading" | "ready" | "unavailable";
  text?: string;
};

type RecognitionRawArtifactViewerProps = {
  artifacts: readonly RecognitionRawArtifact[];
  locale: AdminDashboardLocale;
  onClose: () => void;
  onExpired: () => void;
  recognitionRunId: string;
  requestRawAccess: (input: AdminRecognitionRawAccessRequest) => Promise<AdminRecognitionRawAccessGrant | undefined>;
};

function isExpired(expiresAt: string): boolean {
  return Number.isNaN(Date.parse(expiresAt)) || Date.parse(expiresAt) <= Date.now();
}

export function RecognitionRawArtifactViewer(props: RecognitionRawArtifactViewerProps): JSX.Element {
  const [reason, setReason] = useState("");
  const [requestingId, setRequestingId] = useState<string | undefined>();
  const [loaded, setLoaded] = useState<Record<string, LoadedArtifact>>({});
  const [message, setMessage] = useState<string | undefined>();
  const artifactKey = props.artifacts.map((artifact) => `${artifact.id}:${artifact.action.targetId ?? ""}`).join("|");
  const clearArtifacts = () => {
    setLoaded({});
    setMessage(undefined);
  };
  const copy = useMemo(() => ({
    title: pickAdminDashboardLocaleText(props.locale, { en: "Temporary raw artifact viewer", ko: "임시 원문 뷰어" }),
    request: pickAdminDashboardLocaleText(props.locale, { en: "Request access", ko: "접근 요청" }),
    close: pickAdminDashboardLocaleText(props.locale, { en: "Close and clear", ko: "닫고 지우기" }),
    reason: pickAdminDashboardLocaleText(props.locale, { en: "Reason", ko: "사유" }),
    unavailable: pickAdminDashboardLocaleText(props.locale, { en: "Raw artifact unavailable", ko: "원문을 불러올 수 없습니다" }),
    jsonDiff: pickAdminDashboardLocaleText(props.locale, { en: "Raw JSON diff", ko: "원문 JSON 차이" }),
  }), [props.locale]);

  useEffect(() => {
    clearArtifacts();
    return clearArtifacts;
  }, [artifactKey]);

  useEffect(() => {
    const timers = Object.values(loaded).map(({ grant }) => window.setTimeout(() => {
      clearArtifacts();
      props.onExpired();
    }, Math.max(0, Date.parse(grant.expiresAt) - Date.now())));
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [loaded]);

  useEffect(() => {
    const controller = new AbortController();
    for (const entry of Object.values(loaded)) {
      if ((entry.artifact.kind !== "json" && entry.artifact.kind !== "text") || entry.status !== "loading" || !entry.grant.readUrl) {
        continue;
      }
      void fetch(entry.grant.readUrl, { signal: controller.signal })
        .then((response) => response.ok ? response.text() : Promise.reject(new Error("raw response unavailable")))
        .then((text) => entry.artifact.kind === "json" ? { json: JSON.parse(text) as unknown } : { text })
        .then((content) => setLoaded((current) => current[entry.artifact.id] === entry ? { ...current, [entry.artifact.id]: { ...entry, ...content, status: "ready" } } : current))
        .catch(() => {
          if (!controller.signal.aborted) {
            setLoaded((current) => current[entry.artifact.id] === entry ? { ...current, [entry.artifact.id]: { ...entry, status: "unavailable" } } : current);
          }
        });
    }
    return () => controller.abort();
  }, [loaded]);

  const request = async (artifact: RecognitionRawArtifact) => {
    const validation = validateRecognitionRawAccessReason(reason);
    if (validation) {
      setMessage(validation);
      return;
    }
    if (artifact.action.state !== "available" || !artifact.action.targetId) {
      setMessage(copy.unavailable);
      return;
    }
    setRequestingId(artifact.id);
    setMessage(undefined);
    try {
      const target = {
        recognitionRunId: props.recognitionRunId,
        targetKind: artifact.action.targetKind,
        targetId: artifact.action.targetId,
        purpose: "recognition_quality_review",
        reason: reason.trim(),
      } as const;
      const grant = await props.requestRawAccess(target);
      if (!grant || !isMatchingRawArtifactGrant(target, grant) || isExpired(grant.expiresAt)) {
        setMessage(copy.unavailable);
        return;
      }
      if ((artifact.kind === "json" || artifact.kind === "text") && !grant.readUrl) {
        setMessage(copy.unavailable);
        return;
      }
      if (artifact.kind === "source" && !grant.downloadUrl) {
        setMessage(copy.unavailable);
        return;
      }
      setLoaded((current) => ({ ...current, [artifact.id]: { artifact, grant, status: artifact.kind === "json" || artifact.kind === "text" ? "loading" : "ready" } }));
    } finally {
      setRequestingId(undefined);
    }
  };

  const jsonArtifacts = props.artifacts.map((artifact) => loaded[artifact.id]).filter((entry): entry is LoadedArtifact => entry?.artifact.kind === "json" && entry.status === "ready" && entry.json !== undefined);
  const jsonDiff = jsonArtifacts.length === 2 ? diffJson(jsonArtifacts[0]!.json, jsonArtifacts[1]!.json) : [];

  return (
    <section aria-label={copy.title} style={{ display: "grid", gap: "12px", padding: "16px", border: "1px solid rgba(148, 163, 184, 0.4)", borderRadius: "16px", background: "#fff" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}><strong>{copy.title}</strong><button onClick={() => { clearArtifacts(); props.onClose(); }} type="button">{copy.close}</button></div>
      <label style={{ display: "grid", gap: "6px" }}><span>{copy.reason}</span><textarea onChange={(event) => setReason(event.target.value)} value={reason} /></label>
      {message ? <p style={{ margin: 0, color: "#b91c1c" }}>{message}</p> : null}
      {props.artifacts.map((artifact) => {
        const entry = loaded[artifact.id];
        return <section key={artifact.id} style={{ display: "grid", gap: "8px", padding: "12px", border: "1px solid rgba(148, 163, 184, 0.25)", borderRadius: "12px" }}><strong>{artifact.label}</strong><button disabled={requestingId === artifact.id || artifact.action.state !== "available"} onClick={() => void request(artifact)} type="button">{copy.request}</button>{entry?.status === "unavailable" ? <span>{copy.unavailable}</span> : null}{entry?.artifact.kind === "json" && entry.status === "ready" ? <pre style={{ overflowX: "auto", margin: 0 }}>{JSON.stringify(entry.json, null, 2)}</pre> : null}{entry?.artifact.kind === "text" && entry.status === "ready" ? <pre style={{ overflowX: "auto", margin: 0 }}>{entry.text}</pre> : null}{entry?.artifact.kind === "source" && entry.status === "ready" && artifact.mimeType?.startsWith("image/") && entry.grant.downloadUrl ? <img alt={artifact.label} src={entry.grant.downloadUrl} style={{ maxWidth: "100%" }} /> : null}{entry?.artifact.kind === "source" && entry.status === "ready" && artifact.mimeType === "application/pdf" && entry.grant.downloadUrl ? <iframe sandbox="" src={entry.grant.downloadUrl} style={{ border: 0, minHeight: "480px", width: "100%" }} title={artifact.label} /> : null}</section>;
      })}
      {jsonArtifacts.length === 2 ? <section style={{ display: "grid", gap: "6px" }}><strong>{copy.jsonDiff}</strong>{jsonDiff.length > 0 ? jsonDiff.map((entry) => <span key={`${entry.kind}_${entry.path}`}>{entry.path}: {entry.kind}</span>) : <span>-</span>}</section> : null}
    </section>
  );
}
